import { SetStateAction, useCallback, useMemo } from "react";
import isEqual from "lodash/isEqual";
import z from "zod";

import { createUseDebounce } from "lib/debounce";
import { PaginationOptions } from "lib/generated/app-service-gql/graphql";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { parsePaginationUrlState, useURLState } from "lib/hooks/useURLState";
import { NoticeSemanticColumnFieldEnum } from "lib/types/savedViews";
import {
  DEFAULT_NOTICE_SEMANTIC_COLUMN_FIELDS,
  normalizeNoticeSemanticColumnFields,
} from "./noticeSemanticColumns";
import { noticeSemanticSortSchema } from "./noticeSemanticSort";
import { DEFAULT_PAGINATION } from "./NoticeTableV2";
import { DEFAULT_FILTERS, NoticeV2Filters, NoticeV2FiltersSchema } from "./utils";

const useDebounce400 = createUseDebounce(400);

export const noticeSemanticColumnsSchema = z.array(NoticeSemanticColumnFieldEnum).min(1);

/** Merge a new topic string into URL-backed filters; clearing the topic resets non-topic filters to defaults. */
export function mergeNoticeSemanticTopicSearch(
  prev: NoticeV2Filters | undefined,
  topicSearch: string,
): NoticeV2Filters {
  const base = prev ?? DEFAULT_FILTERS;
  const defaultFiltersWithoutTopic =
    topicSearch.trim() === ""
      ? {
          stage: DEFAULT_FILTERS.stage,
          buyerIds: DEFAULT_FILTERS.buyerIds,
          supplierIds: DEFAULT_FILTERS.supplierIds,
          frameworkIds: DEFAULT_FILTERS.frameworkIds,
          publishDate: DEFAULT_FILTERS.publishDate,
          expiryDate: DEFAULT_FILTERS.expiryDate,
        }
      : {};
  return {
    ...base,
    topicSearch,
    ...defaultFiltersWithoutTopic,
  };
}

export function useNoticesV2UrlState() {
  const [storedPageSize, setStoredPageSize] = useTablePageSize(
    "notice-table-v2",
    DEFAULT_PAGINATION.pageSize,
  );

  const defaultPagination = useMemo(
    (): PaginationOptions => ({
      page: DEFAULT_PAGINATION.page,
      pageSize: storedPageSize,
    }),
    [storedPageSize],
  );

  const [urlFilters, setUrlFilters] = useURLState(
    "noticeSemanticFilters",
    undefined,
    NoticeV2FiltersSchema.optional(),
    { encoder: "compress" },
  );

  const [pagination, setPagination] = useURLState<PaginationOptions>(
    "noticeSemanticPagination",
    defaultPagination,
    parsePaginationUrlState,
    { encoder: "compress" },
  );

  const [urlColumnSettings, setUrlColumnSettings] = useURLState(
    "noticeSemanticColumns",
    undefined,
    noticeSemanticColumnsSchema.optional(),
    { encoder: "compress" },
  );

  const [urlSemanticSort, setUrlSemanticSort] = useURLState(
    "noticeSemanticSort",
    undefined,
    noticeSemanticSortSchema.optional(),
    { encoder: "compress" },
  );

  const setFilters = useCallback(
    (update: SetStateAction<NoticeV2Filters | undefined>) => {
      setUrlFilters((prev) => {
        const next = typeof update === "function" ? update(prev) : update;
        if (next === undefined) {
          return undefined;
        }
        return isEqual(next, DEFAULT_FILTERS) ? undefined : next;
      });
    },
    [setUrlFilters],
  );

  const setTopicSearch = useCallback(
    (topicSearch: string, currentFilters?: NoticeV2Filters) => {
      setFilters((prev) => mergeNoticeSemanticTopicSearch(prev ?? currentFilters, topicSearch));
    },
    [setFilters],
  );

  const debouncedSetFilters = useDebounce400(setFilters);

  const setColumnSettings = useCallback(
    (update: SetStateAction<z.infer<typeof noticeSemanticColumnsSchema> | undefined>) => {
      setUrlColumnSettings((prev) => {
        const raw = typeof update === "function" ? update(prev) : update;
        if (raw === undefined) {
          return undefined;
        }
        const next = normalizeNoticeSemanticColumnFields(raw);
        return isEqual(next, DEFAULT_NOTICE_SEMANTIC_COLUMN_FIELDS) ? undefined : next;
      });
    },
    [setUrlColumnSettings],
  );

  const setPaginationAndPersistPageSize = useCallback(
    (page: number, newPageSize: number) => {
      setPagination({ page, pageSize: newPageSize });
      if (newPageSize !== storedPageSize) {
        setStoredPageSize(newPageSize);
      }
    },
    [setPagination, setStoredPageSize, storedPageSize],
  );

  const resetPaginationToFirstPage = useCallback(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [setPagination]);

  return {
    urlFilters,
    setFilters,
    setTopicSearch,
    debouncedSetFilters,
    urlColumnSettings,
    setColumnSettings,
    urlSemanticSort,
    setSemanticSort: setUrlSemanticSort,
    pagination,
    setPaginationAndPersistPageSize,
    resetPaginationToFirstPage,
  };
}
